import React, { useEffect, useState } from "react";
import HeaderFilter from "./widgets/HeaderFilter";
import PageTitle from "../../Components/layouts/header/page-title";
import moment from "moment";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import { getRequests } from "../../services/employee.service";
import InlineCalendar from "../../Components/UI/InlineCalendar";
import SimpleBar from "simplebar-react";
import { notificationSocket } from "../../utils/notification.socket.service";
import { apiBaseUrl } from "../../constants/environmentConstants/environmentConstants";

interface IRequestOff {
  leaves: IRequest[];
}
interface IRequest {
  monthName: string;
  leaves: ILeaves[];
}
interface ILeaves {
  date: string;
  _id: string;
  month: number;
  status?: string;
}

const STATUS_CLASSES: Record<string, string> = {
  Pending:  "bg-yellow-100 text-yellow-700",
  Applied:  "bg-blue-100 text-blue-700",
  Accepted: "bg-green-100 text-green-700",
  Rejected: "bg-red-100 text-red-700",
};

const TimeOff = () => {
  const { User } = useSelector((state: RootState) => state.auth);
  const [reqTimeOffViewRes, setReqTimeOffViewRes] = useState<IRequestOff>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [month, setMonth] = useState<number>(moment().month() + 1);
  const [fetchAgain, setFetchAgain] = useState<boolean>(false);
  const [selectedDays, setSelectedDays] = useState<IRequest>();
  const [year, setYear] = useState({
    name: moment().format("YYYY"),
    year: moment().format("YYYY"),
    label: "year",
  });

  const fetchRequest = async () => {
    setIsLoading(true);
    const res: any = await getRequests(+year?.year);
    if (res?.res) {
      setReqTimeOffViewRes(res?.res);
      if (res?.res?.leaves?.length > 0) {
        setSelectedDays({ ...res?.res?.leaves[0] });
        const getMonthIndex = moment()
          .month(res?.res?.leaves[0]?.monthName ?? "")
          .format("M");
        setMonth(Number(getMonthIndex));
      }
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchRequest();
  }, [year, fetchAgain]);

  // Re-fetch when manager accepts or declines this nurse's request
  useEffect(() => {
    if (!User?.id || !apiBaseUrl) return;

    // Ensure socket is initialized
    if (!notificationSocket.socket?.connected) {
      notificationSocket.initializeConnection(apiBaseUrl, User.id, User.role);
    }

    const handler = () => {
      console.log("[TimeOff] requestResponse received — refreshing");
      fetchRequest();
    };

    // Retry attaching the listener until socket is ready
    let retries = 0;
    const attach = () => {
      if (notificationSocket.socket) {
        notificationSocket.socket.off("requestResponse", handler);
        notificationSocket.socket.on("requestResponse", handler);
        console.log("[TimeOff] requestResponse listener attached");
      } else if (retries < 10) {
        retries++;
        setTimeout(attach, 300);
      }
    };
    attach();

    return () => {
      notificationSocket?.socket?.off("requestResponse", handler);
    };
  }, [User?.id, year]);

  // Flatten all grouped leaves into a single sorted list
  const allLeaves: (ILeaves & { monthName: string })[] = [];
  reqTimeOffViewRes?.leaves?.forEach((group) => {
    group.leaves?.forEach((leaf) => {
      allLeaves.push({ ...leaf, monthName: group.monthName });
    });
  });
  allLeaves.sort(
    (a, b) =>
      moment(b.date, "MM/DD/YYYY").valueOf() -
      moment(a.date, "MM/DD/YYYY").valueOf()
  );

  const selectedMonthName = moment()
    .month(Number(month) - 1)
    .format("MMMM");

  return (
    <>
      <div className="bg-[#f8f8f8] min-h-screen">
        <PageTitle
          title="Time-off"
          subtitle="Apply and see past time-off requests"
        />
        <div className="flex flex-wrap">
          {/* Left: calendar panel */}
          <div className="w-1/4">
            <div className="border-r border-beerus bg-white shadow-[0_8px_24px_-6px_rgba(0,0,0,0.08)]">
              <div className="bg-white pt-[30px] 2xl:p-[20px] px-4">
                <h3 className="text-moon-20 font-semibold">Month Calendar</h3>
                <p className="text-gray-600 text-moon-14 mb-[15px]">
                  You are seeing the time off days for {selectedMonthName}
                </p>
                <div className="bg-gohan flex flex-col gap-2 rounded-moon-s-lg">
                  <SimpleBar className="overflow-y-auto h-[calc(100vh-285px)]">
                    <div className="flex-1">
                      <InlineCalendar
                        month={month as number}
                        selectedDays={selectedDays?.leaves as ILeaves[]}
                        year={
                          reqTimeOffViewRes?.leaves?.length === 0
                            ? +moment().format("YYYY")
                            : +year?.year
                        }
                      />
                    </div>
                  </SimpleBar>
                </div>
              </div>
            </div>
          </div>

          {/* Right: flat table — same design as Leaves */}
          <div className="w-3/4">
            <div className="p-[20px_30px]">
              {/* Year dropdown + Apply button */}
              <HeaderFilter
                yearDropDown={year}
                setYearDropDown={setYear}
                setFetchAgain={setFetchAgain}
                fetchAgain={fetchAgain}
              />

              <div className="mt-5">
                {isLoading ? (
                  <div className="flex justify-center items-center h-48 text-gray-400 text-moon-14">
                    Loading...
                  </div>
                ) : allLeaves.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-64">
                    <img
                      src={"images/no-schedule-to-approve.svg"}
                      alt="No time-off requests"
                      className="mb-4"
                    />
                    <p className="text-gray-400 text-moon-14">
                      No time-off requests for {year?.year}
                    </p>
                  </div>
                ) : (
                  <div className="bg-white rounded-moon-s-lg shadow-sm overflow-hidden">
                    {/* Table header */}
                    <div className="grid grid-cols-4 gap-4 px-6 py-3 bg-gray-50 border-b border-beerus text-moon-12 font-semibold text-gray-500 uppercase tracking-wide">
                      <div>Date</div>
                      <div>Day</div>
                      <div>Month</div>
                      <div>Status</div>
                    </div>

                    {/* Rows */}
                    <SimpleBar className="h-[calc(100vh-340px)] overflow-y-auto">
                      {allLeaves.map((leaf) => {
                        const d = moment(leaf.date, "MM/DD/YYYY");
                        return (
                          <div
                            key={leaf._id}
                            onClick={() => {
                              // clicking a row updates the calendar to that month
                              const group = reqTimeOffViewRes?.leaves?.find(
                                (g) => g.monthName === leaf.monthName
                              );
                              if (group) {
                                setSelectedDays(group);
                                setMonth(
                                  Number(
                                    moment().month(leaf.monthName).format("M")
                                  )
                                );
                              }
                            }}
                            className="grid grid-cols-4 gap-4 px-6 py-4 border-b border-beerus hover:bg-gray-50 transition-colors items-center cursor-pointer"
                          >
                            <div className="text-moon-14 font-medium text-blackcst">
                              {d.format("DD MMM YYYY")}
                            </div>
                            <div className="text-moon-14 text-gray-600">
                              {d.format("dddd")}
                            </div>
                            <div className="text-moon-14 text-gray-600">
                              {leaf.monthName}
                            </div>
                            <div>
                              <span
                                className={`inline-block px-3 py-1 rounded-full text-moon-12 font-medium ${
                                  STATUS_CLASSES[leaf.status ?? ""] ??
                                  "bg-gray-100 text-gray-600"
                                }`}
                              >
                                {leaf.status ?? "—"}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </SimpleBar>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TimeOff;
